/**
 * MessagingContext - Shared messaging state
 * Manages unread count and real-time updates via polling
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import messagingService from '../services/messagingService';

const MessagingContext = createContext(null);

// No background polling — fetch once on mount only to avoid server load on every page

export const useMessaging = () => {
    const context = useContext(MessagingContext);
    if (!context) {
        throw new Error('useMessaging must be used within a MessagingProvider');
    }
    return context;
};

export const MessagingProvider = ({ children }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastUpdate, setLastUpdate] = useState(Date.now()); // Trigger for conversation list refresh
    const conversationListCallbackRef = useRef(null);

    // Fetch unread count from API
    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await messagingService.getUnreadCount();
            if (response.success) {
                setUnreadCount(response.data?.unread_count || 0);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    }, []);

    // Register callback for conversation list refresh
    const registerConversationListCallback = useCallback((callback) => {
        conversationListCallbackRef.current = callback;
    }, []);

    // Trigger conversation list refresh
    const triggerConversationRefresh = useCallback(() => {
        setLastUpdate(Date.now());
        if (conversationListCallbackRef.current) {
            conversationListCallbackRef.current();
        }
    }, []);

    // No automatic fetch — unread count is loaded on-demand from Messaging page only
    // Call fetchUnreadCount() explicitly from the Messaging page or when needed

    // Decrease unread count when messages are read
    const markConversationRead = useCallback((count = 1) => {
        setUnreadCount(prev => Math.max(0, prev - count));
    }, []);

    const value = {
        unreadCount,
        setUnreadCount,
        fetchUnreadCount,
        markConversationRead,
        lastUpdate,
        registerConversationListCallback,
        triggerConversationRefresh,
    };

    return (
        <MessagingContext.Provider value={value}>
            {children}
        </MessagingContext.Provider>
    );
};

export default MessagingContext;
